import { jsPDF } from "jspdf";

export interface InvoicePdfRow {
  issueDate: string;
  invoiceNumber: string;
  sourceType: string;
  seller: string;
  buyer: string;
  netAmount: number;
  vatAmount: number;
  grossAmount: number;
  currency: string;
}

export interface InvoicesPdfInput {
  rows: InvoicePdfRow[];
  from: string;
  to: string;
  scopeLabel: string;
}

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

const fmtDate = (iso: string) => iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";
const fmtAmt = (amount: number, currency: string) =>
  `${amount.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

export async function generateInvoicesPdf(input: InvoicesPdfInput): Promise<void> {
  if (!fontRegularB64) fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)    fontBoldB64    = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const ML = 10, MT = 10, PW = 277, PH = 190;
  const ROW_H = 7, HDR_H = 9;

  const PAD = 1.5;    // poziomy padding komórki (mm)
  const PAD_V = 1.6;  // pionowy padding komórki (mm)
  // Wysokość pojedynczej linii dla danego rozmiaru czcionki (pt → mm, z interlinią).
  const lineHeightMm = (size: number) => size * 0.3528 * 1.15;

  const cell = (
    x: number, y: number, w: number, _h: number,
    text: string, align: "left" | "right" | "center",
    bold = false, size = 8,
  ) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    const tx = align === "right" ? x + w - PAD : align === "center" ? x + w / 2 : x + PAD;
    // Zawijanie tekstu w granicach komórki — wielolinijkowa treść mieści się
    // w wierszu o dynamicznej wysokości (patrz measureRowHeight), bez nachodzenia.
    const lines = doc.splitTextToSize(text, w - 2 * PAD);
    const ty = y + PAD_V + lineHeightMm(size) * 0.78;
    doc.text(lines, tx, ty, { align, lineHeightFactor: 1.15 });
  };

  let y = MT;
  cell(ML, y, PW, 7, "Faktury i rachunki", "left", true, 13);
  y += 7;
  cell(ML, y, PW, 5,
    `${input.scopeLabel}  ·  ${fmtDate(input.from)} – ${fmtDate(input.to)}  ·  Liczba pozycji: ${input.rows.length}`,
    "left", false, 8);
  y += 6;

  const SPRZEDAWCA_W = 50, NABYWCA_W = 50;
  type ColDef = { header: string; w: number; align: "left" | "right" };
  const COLS: ColDef[] = [
    { header: "Data",       w: 22,          align: "left"  },
    { header: "Numer",      w: 36,          align: "left"  },
    { header: "Typ",        w: 14,          align: "center"},
    { header: "Sprzedawca", w: SPRZEDAWCA_W, align: "left" },
    { header: "Nabywca",    w: NABYWCA_W,   align: "left"  },
    { header: "Netto",      w: 32,          align: "right" },
    { header: "VAT",        w: 32,          align: "right" },
    { header: "Brutto",     w: 32,          align: "right" },
  ];
  const fixedW = COLS.reduce((s, c) => s + c.w, 0);
  const remaining = PW - fixedW;
  COLS[3].w += Math.floor(remaining / 2);
  COLS[4].w += remaining - Math.floor(remaining / 2);

  const drawTableHeader = (yy: number) => {
    doc.setFillColor(235, 232, 250);
    doc.rect(ML, yy, PW, HDR_H, "F");
    let x = ML;
    COLS.forEach((col) => { cell(x, yy, col.w, HDR_H, col.header, col.align, true, 8); x += col.w; });
    doc.setDrawColor(180, 170, 210);
    doc.line(ML, yy + HDR_H, ML + PW, yy + HDR_H);
    return yy + HDR_H;
  };

  y = drawTableHeader(y);

  // Wysokość wiersza dopasowana do najdłuższej (najbardziej zawiniętej) komórki,
  // aby tekst zawsze mieścił się w granicach wiersza. Minimum = ROW_H.
  const measureRowHeight = (vals: string[]): number => {
    doc.setFont("LiberationSans", "normal");
    doc.setFontSize(7.5);
    let maxLines = 1;
    COLS.forEach((col, ci) => {
      const lines = doc.splitTextToSize(vals[ci] || "", col.w - 2 * PAD);
      if (lines.length > maxLines) maxLines = lines.length;
    });
    return Math.max(ROW_H, maxLines * lineHeightMm(7.5) + 2 * PAD_V);
  };

  let totalNet = 0, totalVat = 0, totalGross = 0;

  input.rows.forEach((row, i) => {
    totalNet   += row.netAmount;
    totalVat   += row.vatAmount;
    totalGross += row.grossAmount;

    const cur = row.currency || "PLN";
    const vals = [
      row.issueDate ? fmtDate(row.issueDate) : "—",
      row.invoiceNumber || "—",
      row.sourceType || "—",
      row.seller || "—",
      row.buyer || "—",
      row.netAmount   ? fmtAmt(row.netAmount,   cur) : "—",
      row.vatAmount   ? fmtAmt(row.vatAmount,   cur) : "—",
      row.grossAmount ? fmtAmt(row.grossAmount, cur) : "—",
    ];

    // Wysokość wiersza dopasowana do treści (wielolinijkowy Sprzedawca / Nabywca).
    const rowH = measureRowHeight(vals);

    if (y + rowH > MT + PH) { doc.addPage(); y = MT; y = drawTableHeader(y); }

    if (i % 2 === 0) { doc.setFillColor(250, 248, 255); doc.rect(ML, y, PW, rowH, "F"); }

    let x = ML;
    COLS.forEach((col, ci) => {
      doc.setTextColor(28, 27, 31);
      cell(x, y, col.w, rowH, vals[ci], col.align, false, 7.5);
      x += col.w;
    });
    doc.setTextColor(0, 0, 0);

    doc.setDrawColor(220, 215, 230);
    doc.line(ML, y + rowH, ML + PW, y + rowH);
    y += rowH;
  });

  if (y + ROW_H > MT + PH) { doc.addPage(); y = MT; }
  doc.setFillColor(220, 215, 240);
  doc.rect(ML, y, PW, ROW_H, "F");
  const cur = input.rows[0]?.currency || "PLN";
  const sumLabel = `Netto: ${fmtAmt(totalNet, cur)}   VAT: ${fmtAmt(totalVat, cur)}   Brutto: ${fmtAmt(totalGross, cur)}`;
  cell(ML, y, PW - 2, ROW_H, sumLabel, "right", true, 7.5);

  doc.save(`faktury-${input.from}-${input.to}.pdf`);
}
